/**
 * Utility function to extract all available photos from a post
 * Combines post.image_urls and photos from all postLocations
 * 
 * Priority order:
 * 1. Post's own image_urls (cover photos)
 * 2. PostLocation image_urls (user-uploaded photos)
 * 3. Location Google Places photos (from location.photos and location.google_places_data.photos)
 */

import { extractPhotosFromLocation, getPhotoUrl, type LocationDataWithPhotos, type PhotoIdentifier } from './photoUtils';
import { debug } from '@/utils/logger';

// Constants
const MAX_COVER_PHOTOS_IF_ALL_GOOGLE = 2;
const MAX_GOOGLE_PHOTOS_PER_LOCATION = 1;
const MAX_TOTAL_GOOGLE_PHOTOS = 30;
const CAROUSEL_PHOTO_SIZE = 800;
const SAMPLE_URL_COUNT = 3;
const SAMPLE_URL_TRUNCATE_LENGTH = 100;

interface LocationWithPhotos {
  name?: string;
  photos?: Array<{ name?: string; photo_reference?: string; url?: string } | string> | null;
  google_places_data?: {
    photos?: Array<{ name?: string; photo_reference?: string; url?: string } | string> | null;
  } | null;
  image_urls?: string[] | null;
}

interface PostLocationWithPhotos {
  image_urls?: Array<string | { url?: string } | PhotoIdentifier> | null;
  location?: LocationWithPhotos | null;
}

interface CityWithPhoto {
  largePhotoURI?: string;
  thumbnailPhotoURI?: string;
}

interface PostWithPhotos {
  _id?: string;
  post_type?: string;
  image_urls?: Array<string | { url?: string } | PhotoIdentifier> | null;
  post_locations?: PostLocationWithPhotos[] | null;
  postLocations?: PostLocationWithPhotos[] | null;
  /** City objects from the backend — used as cover photo fallback for List posts */
  cities?: CityWithPhoto[] | null;
}

/**
 * Extract all available photos from a post for carousel display
 * 
 * Limits:
 * - Post cover photos: All if user-uploaded or mixed, max 2 if all are Google photos
 * - User-uploaded photos from locations: All (no limit)
 * - Google Places photos: Max 1 per location, max 30 total
 * 
 * @param post - Post object with image_urls and postLocations
 * @returns Array of photo URLs ready for carousel display
 */
export function extractAllPostPhotos(post: PostWithPhotos | null | undefined): string[] {
  if (!post) {
    return [];
  }

  const allPhotoUrls: string[] = [];
  const rawPl = post.post_locations ?? post.postLocations;
  const postLocations = Array.isArray(rawPl) ? rawPl : [];

  // Helper to normalize image URL (handle both string and object formats)
  const normalizeImageUrl = (
    img: string | { url?: string } | PhotoIdentifier | null | undefined
  ): string | null => {
    if (!img) return null;
    if (typeof img === 'string') return img;
    if (typeof img !== 'object') return null;
    // Use `in` to narrow the union safely — PhotoIdentifier has no `url` field
    // and the legacy { url? } shape has neither `name` nor `photo_reference`.
    if ('url' in img && typeof img.url === 'string' && img.url.trim().length > 0) {
      return img.url;
    }
    if (
      ('name' in img && typeof img.name === 'string') ||
      ('photo_reference' in img && typeof img.photo_reference === 'string')
    ) {
      return getPhotoUrl(img as PhotoIdentifier, CAROUSEL_PHOTO_SIZE) || null;
    }
    return null;
  };

  // Helper to detect if a URL is from Instagram/TikTok CDN (blocked for hotlinking)
  const isBlockedCDNUrl = (url: string | null | undefined): boolean => {
    if (!url || typeof url !== 'string') return false;
    const lowerUrl = url.toLowerCase();
    return (
      lowerUrl.includes('cdninstagram.com') ||
      lowerUrl.includes('instagram.com') ||
      (lowerUrl.includes('instagram.') && lowerUrl.includes('fbcdn.net')) ||
      lowerUrl.includes('fbcdn.net') ||
      lowerUrl.includes('tiktokcdn.com') ||
      (lowerUrl.includes('scontent') && (lowerUrl.includes('instagram') || lowerUrl.includes('fbcdn'))) ||
      lowerUrl.includes('scontent.cdninstagram.com')
    );
  };

  // Helper to detect if a URL is a Google photo (not user-uploaded)
  const isGooglePhoto = (url: string | null | undefined): boolean => {
    if (!url || typeof url !== 'string') return false;
    // Google photos come from our proxy (api.globethrivers.com/api/v1/public/places/photo)
    // or app-be-stage.globethrivers.com/api/v1/public/places/photo (staging)
    // or maps.googleapis.com
    return url.includes('/api/v1/public/places/photo') || 
           url.includes('google-photos') || 
           url.includes('maps.googleapis.com');
  };

  // Helper to detect if a URL is a user-uploaded photo from S3
  const isUserUploadedPhoto = (url: string | null | undefined): boolean => {
    if (!url || typeof url !== 'string') return false;
    const isS3Url = url.includes('.s3.') || url.includes('amazonaws.com');
    return isS3Url && !isGooglePhoto(url);
  };

  // Helper to detect if a URL is a video file (should not be used with Next.js Image)
  const isVideoFile = (url: string | null | undefined): boolean => {
    if (!url || typeof url !== 'string') return false;
    const lowerUrl = url.toLowerCase();
    return (
      lowerUrl.endsWith('.mp4') ||
      lowerUrl.endsWith('.mov') ||
      lowerUrl.endsWith('.webm') ||
      lowerUrl.endsWith('.avi') ||
      lowerUrl.includes('.mp4?') ||
      lowerUrl.includes('.mov?') ||
      lowerUrl.includes('.webm?')
    );
  };

  // 1. Post's own image_urls (cover photos - highest priority)
  // Limit: If all are Google photos, limit to 2. If user-uploaded or mixed, no limit.
  // Filter out video files (Next.js Image doesn't support videos) and blocked CDN URLs
  if (post.image_urls && post.image_urls.length > 0) {
    const postImageUrls = post.image_urls
      .map(normalizeImageUrl)
      .filter((url): url is string => Boolean(url))
      .filter((url) => !isVideoFile(url))
      .filter((url) => !isBlockedCDNUrl(url));
    
    // Check if all cover photos are Google photos
    const googlePhotoCount = postImageUrls.filter(url => isGooglePhoto(url)).length;
    const userUploadedCount = postImageUrls.filter(url => isUserUploadedPhoto(url)).length;
    const isAllGooglePhotos = postImageUrls.length > 0 && 
      postImageUrls.every(url => isGooglePhoto(url)) &&
      !postImageUrls.some(url => isUserUploadedPhoto(url));
    
    debug('[extractAllPostPhotos] Analyzing post cover photos', {
      totalAvailable: postImageUrls.length,
      googlePhotoCount,
      userUploadedCount,
      isAllGooglePhotos,
      sampleUrls: postImageUrls.slice(0, SAMPLE_URL_COUNT).map(url => ({
        url: url.substring(0, SAMPLE_URL_TRUNCATE_LENGTH) + '...',
        isGoogle: isGooglePhoto(url),
        isUserUploaded: isUserUploadedPhoto(url),
      })),
    });
    
    let coverPhotosToAdd: string[];
    if (isAllGooglePhotos) {
      // Limit to MAX_COVER_PHOTOS_IF_ALL_GOOGLE if all are Google photos
      coverPhotosToAdd = postImageUrls.slice(0, MAX_COVER_PHOTOS_IF_ALL_GOOGLE);
      debug('[extractAllPostPhotos] Limited post cover photos (all Google)', {
        totalAvailable: postImageUrls.length,
        added: coverPhotosToAdd.length,
        limit: MAX_COVER_PHOTOS_IF_ALL_GOOGLE,
      });
    } else {
      // No limit if user-uploaded or mixed
      coverPhotosToAdd = postImageUrls;
    }
    
    allPhotoUrls.push(...coverPhotosToAdd);
    
    debug('[extractAllPostPhotos] Added post image_urls', {
      count: coverPhotosToAdd.length,
      totalAvailable: postImageUrls.length,
      isAllGooglePhotos,
      urls: coverPhotosToAdd,
    });
  }

  // 2. PostLocation image_urls (user-uploaded photos from locations, no limit)
  // Filter out Google photos - these should only come from step 3
  // Filter out video files (Next.js Image doesn't support videos) and blocked CDN URLs
  for (const pl of postLocations) {
    if (pl?.image_urls && pl.image_urls.length > 0) {
      const plImageUrls = pl.image_urls
        .map(normalizeImageUrl)
        .filter((url): url is string => Boolean(url))
        .filter((url) => !isVideoFile(url))
        .filter((url) => !isBlockedCDNUrl(url))
        .filter(url => !isGooglePhoto(url)); // Exclude Google photos - they're handled in step 3
      allPhotoUrls.push(...plImageUrls);
      
      if (plImageUrls.length < pl.image_urls.length) {
        const filteredCount = pl.image_urls.length - plImageUrls.length;
        debug('[extractAllPostPhotos] Filtered Google photos from PostLocation.image_urls', {
          locationName: pl.location?.name,
          totalAvailable: pl.image_urls.length,
          userUploadedAdded: plImageUrls.length,
          googlePhotosFiltered: filteredCount,
        });
      }
    }
  }

  // 3. Location Google Places photos (from all postLocations)
  // Limit: Max 1 photo per location, max 30 total Google photos
  let totalGooglePhotosAdded = 0;

  for (const pl of postLocations) {
    if (pl?.location && totalGooglePhotosAdded < MAX_TOTAL_GOOGLE_PHOTOS) {
      // Type assertion is safe here - LocationWithPhotos is compatible with LocationDataWithPhotos
      // The function handles null/undefined and extracts photos from the compatible structure
      const locationPhotos = extractPhotosFromLocation(pl.location as LocationDataWithPhotos, CAROUSEL_PHOTO_SIZE);
      // Limit photos per location
      const limitedLocationPhotos = locationPhotos.slice(0, MAX_GOOGLE_PHOTOS_PER_LOCATION);
      // Limit total Google photos
      const remainingSlots = MAX_TOTAL_GOOGLE_PHOTOS - totalGooglePhotosAdded;
      const photosToAdd = limitedLocationPhotos.slice(0, remainingSlots);
      
      if (photosToAdd.length > 0) {
        allPhotoUrls.push(...photosToAdd);
        totalGooglePhotosAdded += photosToAdd.length;

        if (locationPhotos.length > MAX_GOOGLE_PHOTOS_PER_LOCATION) {
          debug('[extractAllPostPhotos] Limited Google photos per location', {
            locationName: pl.location?.name,
            totalAvailable: locationPhotos.length,
            added: photosToAdd.length,
          });
        }
      } else if (pl.image_urls && pl.image_urls.length > 0 && totalGooglePhotosAdded < MAX_TOTAL_GOOGLE_PHOTOS) {
        // location.photos is empty (e.g. newly created location from inspo-import that the backend
        // hasn't fetched Google data for yet). Fall back to the first Google proxy URL from
        // postLocation.image_urls — these are the same proxy URLs the place card thumbnail already
        // shows successfully, so we know they load.
        const firstProxyUrl = (pl.image_urls as Array<string | { url?: string } | PhotoIdentifier>)
          .map((img) => normalizeImageUrl(img))
          .find((url): url is string => Boolean(url) && isGooglePhoto(url));
        if (firstProxyUrl) {
          allPhotoUrls.push(firstProxyUrl);
          totalGooglePhotosAdded += 1;
          debug('[extractAllPostPhotos] Using postLocation.image_urls proxy as location photo fallback', {
            locationName: pl.location?.name,
            url: firstProxyUrl.substring(0, 80),
          });
        }
      }
    }
  }

  if (totalGooglePhotosAdded >= MAX_TOTAL_GOOGLE_PHOTOS) {
    debug('[extractAllPostPhotos] Reached max total Google photos limit', {
      maxLimit: MAX_TOTAL_GOOGLE_PHOTOS,
      totalAdded: totalGooglePhotosAdded,
    });
  }

  // Fallback for List posts: if no cover photos have been collected yet, use the city
  // Google photo so the hero always shows the city image rather than the default palm tree.
  // This covers the case where a user deleted their uploaded photo and the DB has empty image_urls.
  if (allPhotoUrls.length === 0 && post.post_type === 'List' && post.cities?.length) {
    const cityPhoto = post.cities[0].largePhotoURI || post.cities[0].thumbnailPhotoURI;
    if (cityPhoto) {
      allPhotoUrls.push(cityPhoto);
      debug('[extractAllPostPhotos] Using city Google photo as cover fallback', {
        postId: post._id,
        cityPhoto,
      });
    }
  }

  // Remove duplicates and filter out empty strings
  const uniquePhotoUrls = Array.from(new Set(allPhotoUrls.filter(Boolean)));

  debug('[extractAllPostPhotos] Extracted all post photos', {
    postId: post._id,
    totalPhotos: uniquePhotoUrls.length,
    postImageUrlsCount: post.image_urls?.length || 0,
    postLocationsCount: postLocations.length,
    googlePhotosAdded: totalGooglePhotosAdded,
  });

  return uniquePhotoUrls;
}

